/**
 * Upload brand assets (company logo, person avatar) to Firebase Storage.
 * Paths: brandAssets/{uid}/company-logo, brandAssets/{uid}/person-avatar
 * Max 2MB; types: image/png, image/jpeg, image/webp
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebaseConfig.js';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024; // 2MB

/**
 * @param {File} file
 * @param {string} userId
 * @param {'company-logo' | 'person-avatar'} kind
 * @returns {Promise<string>} download URL
 */
export async function uploadBrandAsset(file, userId, kind) {
  if (!file || !userId) {
    throw new Error('File and user ID are required');
  }
  if (kind !== 'company-logo' && kind !== 'person-avatar') {
    throw new Error('Kind must be company-logo or person-avatar');
  }
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Allowed types: PNG, JPEG, WebP');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Image must be 2MB or less');
  }

  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `brandAssets/${userId}/${kind}_${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  const contentType = file.type || 'image/jpeg';

  await uploadBytes(storageRef, file, { contentType });
  return getDownloadURL(storageRef);
}
